import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Flex,
  Icon,
  Input,
  Skeleton,
  Text,
  toast,
} from '@kognitos/lattice';

import {
  Bdkv2CredentialType,
  Bdkv2OAuthFlow,
  type Bdkv2BookAuthenticationDescriptor,
  type Bdkv2BookDescriptor,
} from '@/generated/hooks/bdk/models';
import {
  V1SecretState,
  type V1OAuthApp,
} from '@/generated/hooks/gringotts/models';
import { useGetSecret2 } from '@/generated/hooks/gringotts/v1/services/secret_service';
import {
  createWithAvailableName,
  ECredentialScope,
  getCredentialScope,
  CredentialActionsMenu,
  CredentialStatusDot,
  useCredentialPermissions,
  useRenameCredential,
  useCompatibleCredentials,
  useCreateCredential,
  useCreateOAuthCredential,
  type ICredentialCreateFormValues,
  type TCredential,
} from '@/modules/credentials';
import { captureException } from '@/modules/sentry';
import { getNoAutofillProps } from '@/shared/utils';
import { usePermissions } from '@/shared/permissions';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';

import {
  useManagedCredential,
  type IResolvedCredential,
} from '../hooks/use-managed-credential';
import { oauthArgumentsToFill } from '../hooks/use-shared-authentications';
import { getKeyValueCredentialIds } from '../utils';
import { EditCredentialValuesDialog } from './EditCredentialValuesDialog';
import {
  EmptyCredentialValuesConfirmDialog,
  useEmptyCredentialValuesConfirmation,
} from './EmptyCredentialValuesConfirmDialog';
import { ManagedCredentialStep } from './ManagedCredentialStep';
import { OAuthAppPicker } from './OAuthAppPicker';
import { OAuthSignInButton, getOAuthProviderConfig } from './OAuthSignInButton';

/** Props for {@link ManagedConnectionCredential}. */
export interface IManagedConnectionCredentialProps {
  integration: Bdkv2BookDescriptor;
  /** The connection's auth method, as the book declares it. */
  authentication: Bdkv2BookAuthenticationDescriptor;
  /** Unique per environment, e.g. `draft` / `published`. */
  idPrefix: string;
  /** The credential the connection uses now; none for a connection still to be created. */
  currentCredentialId?: string;
  /** The credential picked in this edit, when it replaces the current one. */
  selectedCredentialId?: string;
  /** `undefined` drops the pick and keeps the current credential. */
  onSelect: (credentialId: string | undefined) => void;
  /** Name a new credential gets unless renamed. */
  defaultDisplayName: string;
  /** The shared OAuth app covering the auth method, if any. */
  sharedApp?: V1OAuthApp;
  /** The organization's and workspace's own OAuth apps for the auth method. */
  ownApps: V1OAuthApp[];
  /**
   * Sends the browser to the provider to sign a new credential in; false when
   * it can't (the sign-in was undone and reported).
   */
  onRedirect: (authorizeUrl: string, credentialId: string) => boolean;
  /** Starts with the picker open, e.g. back from a failed sign-in. */
  defaultOpen?: boolean;
  /** Shown above the picker, e.g. a failed sign-in. */
  error?: string;
  disabled?: boolean;
  /** Shows the credential in use without offering to change or manage it. */
  readOnly?: boolean;
  /**
   * The connection has credentials set on it directly instead of a managed
   * one; they're kept unless another credential is picked.
   */
  usesInlineCredentials?: boolean;
  /**
   * Called when values typed into the credential's edit form become, or stop
   * being, unsaved. They're saved with the credential, not the connection.
   */
  onUnsavedEditChange?: (hasUnsavedEdit: boolean) => void;
}

/**
 * The credential of one environment of a connection being edited: a row naming
 * the credential in use, with "Change" opening the same picker as the connect
 * wizard. Picking or creating a credential only changes the selection; the
 * dialog's Save re-points the connection.
 *
 * @param props - Component props.
 * @returns The credential row, or the picker while changing it.
 */
export function ManagedConnectionCredential({
  integration,
  authentication,
  idPrefix,
  currentCredentialId,
  selectedCredentialId,
  onSelect,
  defaultDisplayName,
  sharedApp,
  ownApps,
  onRedirect,
  defaultOpen = false,
  error,
  disabled = false,
  readOnly = false,
  usesInlineCredentials = false,
  onUnsavedEditChange,
}: IManagedConnectionCredentialProps) {
  const { t } = useTranslation('credentials');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const { hasPermission } = usePermissions();
  const canChooseScope = hasPermission(['CreateSecret'], {
    organization: organizationId,
  });

  const shownCredentialId = selectedCredentialId ?? currentCredentialId;
  const [isChanging, setIsChanging] = useState(
    !readOnly &&
      (defaultOpen || (!shownCredentialId && !usesInlineCredentials)),
  );
  const [prefersOwnApp, setPrefersOwnApp] = useState(false);
  const [chosenOwnAppId, setChosenOwnAppId] = useState<string>();
  const [ownAppError, setOwnAppError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const secretTypeName = authentication.secret_type_id ?? '';
  const {
    credentials,
    secretType,
    isLoading: isLoadingCredentials,
    isError: isCredentialsError,
  } = useCompatibleCredentials(
    organizationId,
    workspaceId,
    secretTypeName,
    // Read-only rows only show the credential in use, read below.
    !readOnly,
  );
  // The credential in use may not be in the list (e.g. one managed by
  // Kognitos), so it's read on its own.
  const listedCredential = credentials.find(
    credential => credential.id === shownCredentialId,
  );
  const shownCredentialQuery = useGetSecret2(
    organizationId,
    workspaceId,
    shownCredentialId ?? '',
    {
      query: {
        enabled:
          !!shownCredentialId && !isLoadingCredentials && !listedCredential,
        retry: false,
      },
    },
  );
  const shownCredential: TCredential | undefined =
    listedCredential ?? shownCredentialQuery.data?.data;
  const isLoadingShownCredential =
    !!shownCredentialId &&
    !shownCredential &&
    (isLoadingCredentials || shownCredentialQuery.isLoading);

  const isOAuth = !!authentication.oauth;
  const isAuthorizationCode = !!authentication.oauth?.flows?.includes(
    Bdkv2OAuthFlow.OAuthFlowAuthorizationCode,
  );
  const usesOwnApp = isOAuth && (!sharedApp || prefersOwnApp);
  const selectedOwnApp =
    ownApps.find(app => app.id === chosenOwnAppId) ??
    (!chosenOwnAppId && ownApps.length === 1 ? ownApps[0] : undefined);
  const activeApp = usesOwnApp ? selectedOwnApp : sharedApp;
  const providerName = isOAuth
    ? getOAuthProviderConfig(integration, authentication).name
    : undefined;

  const { createCredential } = useCreateCredential(organizationId, workspaceId);
  const { createOAuthCredential } = useCreateOAuthCredential(
    organizationId,
    workspaceId,
  );

  const oauthArguments = useMemo(
    () =>
      isOAuth
        ? (activeApp ? oauthArgumentsToFill(activeApp) : []).map(argument => ({
            id: argument.id,
            label: argument.label || argument.id,
            type: Bdkv2CredentialType.CredentialTypeText,
          }))
        : undefined,
    [isOAuth, activeApp],
  );

  const create = async ({
    displayName,
    scope,
    values,
  }: ICredentialCreateFormValues): Promise<IResolvedCredential> => {
    if (activeApp) {
      return createOAuthCredential({
        displayName,
        secretTypeName,
        oauthAppId: activeApp.id ?? '',
        arguments: values,
      });
    }
    const credentialId = await createCredential({
      scope,
      displayName,
      secretTypeName,
      schema: secretType?.schema,
      values,
    });
    return { credentialId };
  };

  // Changes to the credential itself (its name, and a key/value credential's
  // values) save on their own, since they apply to every connection using it.
  const valueFields = authentication.custom?.credentials ?? [];
  const [isEditingValues, setIsEditingValues] = useState(false);
  const keyValueIds = getKeyValueCredentialIds(
    integration,
    authentication.custom?.id ?? '',
  );
  const { confirmEmptyValues, dialogProps: emptyValuesDialogProps } =
    useEmptyCredentialValuesConfirmation();

  const currentName = shownCredential?.display_name || shownCredential?.name;
  const { rename } = useRenameCredential(organizationId, workspaceId);
  const permissions = useCredentialPermissions(organizationId, workspaceId);
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameError, setNameError] = useState<string>();
  const [isSavingName, setIsSavingName] = useState(false);

  const hasUnsavedEdit = isRenaming && nameDraft.trim() !== currentName;
  // eslint-disable-next-line no-restricted-syntax -- reason: tells the dialog its Save must wait for the credential's own save
  useEffect(() => {
    onUnsavedEditChange?.(hasUnsavedEdit);
  }, [hasUnsavedEdit, onUnsavedEditChange]);

  const startRename = () => {
    setNameDraft(currentName ?? '');
    setNameError(undefined);
    setIsRenaming(true);
  };

  const cancelRename = () => {
    setNameError(undefined);
    setIsRenaming(false);
  };

  const saveRename = async () => {
    if (!shownCredentialId || isSavingName) return;
    if (nameDraft.trim() === currentName) {
      cancelRename();
      return;
    }
    setIsSavingName(true);
    try {
      const error = await rename(shownCredentialId, nameDraft);
      if (error) {
        setNameError(error);
        return;
      }
      setIsRenaming(false);
    } catch {
      setNameError(t('credentials.errors.updateFailed'));
    } finally {
      setIsSavingName(false);
    }
  };

  const managed = useManagedCredential({
    fieldDescriptors:
      oauthArguments ?? authentication.custom?.credentials ?? [],
    defaultDisplayName,
    defaultToCreate: false,
    create,
  });
  const isCreating = managed.selection?.mode === 'create';

  /** Uses a credential just created, or leaves to sign it in. */
  const finish = ({ credentialId, authorizeUrl }: IResolvedCredential) => {
    if (authorizeUrl) {
      // Stays submitting while the browser leaves for the provider.
      if (!onRedirect(authorizeUrl, credentialId)) {
        setIsSubmitting(false);
        managed.reset();
      }
      return;
    }
    onSelect(credentialId);
    setIsSubmitting(false);
    setPrefersOwnApp(false);
    managed.reset();
  };

  const resolveNewCredential = async () => {
    setIsSubmitting(true);
    const resolved = await managed.resolveCredential();
    if (!resolved) {
      setIsSubmitting(false);
      return;
    }
    finish(resolved);
  };

  const submitCreate = () => {
    if (usesOwnApp && !selectedOwnApp) {
      setOwnAppError(t('credentials.oauthApp.selectionRequired'));
      return;
    }
    confirmEmptyValues(
      keyValueIds.map(id => managed.form.getValues(`values.${id}`)),
      () => void resolveNewCredential(),
    );
  };

  /** Signs another account in through the shared app, asking for its arguments first. */
  const signInWithSharedApp = async () => {
    if (!sharedApp) return;
    if (oauthArgumentsToFill(sharedApp).length > 0) {
      managed.setSelection({ mode: 'create' });
      return;
    }
    setIsSubmitting(true);
    try {
      // No name field here to report a conflict on, so take the next free name.
      finish(
        await createWithAvailableName(defaultDisplayName, displayName =>
          createOAuthCredential({
            displayName,
            secretTypeName,
            oauthAppId: sharedApp.id ?? '',
            arguments: {},
          }),
        ),
      );
    } catch (signInError) {
      captureException(signInError as Error, {
        tags: { module: 'integrations', action: 'editSharedAppSignIn' },
      });
      toast.error(t('credentials.errors.createFailed'));
      setIsSubmitting(false);
    }
  };

  const signInButton = (onClick: () => void, isDisabled = false) => (
    <OAuthSignInButton
      integration={integration}
      authentication={authentication}
      disabled={disabled || isDisabled}
      isLoading={isSubmitting}
      onClick={onClick}
    />
  );

  if (!isChanging) {
    const scope = shownCredential ? getCredentialScope(shownCredential) : null;
    const canRename =
      !readOnly && !!shownCredential && permissions.canRename(shownCredential);
    const canEditValues =
      !readOnly &&
      valueFields.length > 0 &&
      !!shownCredential &&
      permissions.canEditValues(shownCredential);
    return (
      <Flex direction="column" gap={2} width="full">
        <Text weight="medium" level="small">
          {t('credentials.picker.label')}
        </Text>
        <Flex
          align="center"
          justify="between"
          gap={3}
          width="full"
          className="rounded-lg border p-3"
          data-testid={`${idPrefix}-row`}
        >
          {isLoadingShownCredential ? (
            <Skeleton className="h-5 w-48" />
          ) : shownCredential ? (
            <Flex align="center" gap={2} className="min-w-0 flex-1">
              {isRenaming ? (
                <Flex direction="column" gap={1} className="min-w-0 flex-1">
                  <Input
                    {...getNoAutofillProps()}
                    autoFocus
                    aria-label={t('credentials.create.displayName.label')}
                    value={nameDraft}
                    disabled={isSavingName}
                    onChange={event => {
                      setNameDraft(event.target.value);
                      setNameError(undefined);
                    }}
                    onBlur={() => void saveRename()}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        void saveRename();
                      } else if (event.key === 'Escape') {
                        // Keeps the dialog open; Escape only cancels the rename.
                        event.preventDefault();
                        event.stopPropagation();
                        cancelRename();
                      }
                    }}
                  />
                  {nameError && (
                    <Text level="small" className="text-destructive">
                      {nameError}
                    </Text>
                  )}
                </Flex>
              ) : (
                <>
                  <CredentialStatusDot credential={shownCredential} />
                  <Text className="truncate">{currentName}</Text>
                </>
              )}
              <Badge variant="outline">
                {scope === ECredentialScope.Organization
                  ? t('credentials.scope.organization')
                  : scope === ECredentialScope.Workspace
                    ? t('credentials.scope.workspace')
                    : t('credentials.managedByKognitos')}
              </Badge>
              {!shownCredential.oauth_app_id &&
                shownCredential.state ===
                  V1SecretState.SECRET_STATE_DEGRADED && (
                  <Badge variant="destructive">
                    {t('credentials.state.degraded')}
                  </Badge>
                )}
            </Flex>
          ) : usesInlineCredentials ? (
            <Text color="muted">{t('credentials.connection.inline')}</Text>
          ) : (
            <Flex align="center" gap={2} className="min-w-0">
              <Icon type="AlertTriangle" size="sm" />
              <Text color="muted">
                {t('credentials.connection.unavailable')}
              </Text>
            </Flex>
          )}
          {!readOnly && (
            <Flex align="center" gap={1} className="shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => {
                  cancelRename();
                  setIsChanging(true);
                }}
              >
                {t('credentials.connection.change')}
              </Button>
              <CredentialActionsMenu
                disabled={disabled}
                actions={{
                  onRename: canRename ? startRename : undefined,
                  onEditValues: canEditValues
                    ? () => setIsEditingValues(true)
                    : undefined,
                }}
              />
            </Flex>
          )}
        </Flex>
        {isOAuth && isAuthorizationCode && shownCredential && (
          <Text level="small" color="muted">
            {t('credentials.connection.reconnectHint')}
          </Text>
        )}
        {authentication.custom && (
          <EditCredentialValuesDialog
            organizationId={organizationId}
            workspaceId={workspaceId}
            integration={integration}
            authentication={authentication}
            credential={isEditingValues ? (shownCredential ?? null) : null}
            onClose={() => setIsEditingValues(false)}
          />
        )}
        <EmptyCredentialValuesConfirmDialog {...emptyValuesDialogProps} />
      </Flex>
    );
  }

  const returnsToList = isCreating && credentials.length > 0;
  const createActions = isCreating && (
    <Flex justify="end" gap={2}>
      {!(isOAuth && isAuthorizationCode) && (
        <Button
          type="button"
          onClick={submitCreate}
          disabled={disabled}
          isLoading={isSubmitting}
        >
          {t('credentials.create.submit')}
        </Button>
      )}
    </Flex>
  );

  return (
    <Flex direction="column" gap={3} width="full">
      {error && (
        <Alert variant="destructive">
          <Icon type="AlertTriangle" size="sm" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <ManagedCredentialStep
        integration={integration}
        authMethodId={
          authentication.custom?.id || authentication.oauth?.id || ''
        }
        idPrefix={idPrefix}
        credentials={credentials}
        isLoading={isLoadingCredentials}
        isError={isCredentialsError}
        selection={
          isCreating
            ? { mode: 'create' }
            : shownCredentialId
              ? { mode: 'existing', credentialId: shownCredentialId }
              : null
        }
        onSelectionChange={selection => {
          if (selection?.mode === 'existing') {
            onSelect(selection.credentialId);
            return;
          }
          if (!selection) {
            setPrefersOwnApp(false);
            // E.g. the picked credential was deleted: back to the current one.
            onSelect(undefined);
          }
          managed.setSelection(selection);
        }}
        form={managed.form}
        defaultDisplayName={defaultDisplayName}
        canChooseScope={canChooseScope}
        oauthArguments={oauthArguments}
        oauthProviderName={providerName}
        oauthSignIn={
          sharedApp && !prefersOwnApp
            ? {
                authentication,
                onClick: () => void signInWithSharedApp(),
                isLoading: isSubmitting,
              }
            : undefined
        }
        appPicker={
          usesOwnApp ? (
            <OAuthAppPicker
              integration={integration}
              authentication={authentication}
              secretTypeName={secretTypeName}
              apps={ownApps}
              selectedAppId={selectedOwnApp?.id}
              onSelect={appId => {
                setChosenOwnAppId(appId);
                setOwnAppError(undefined);
              }}
              canChooseScope={canChooseScope}
              error={ownAppError}
            />
          ) : undefined
        }
        inlineSignIn={
          isCreating &&
          isAuthorizationCode &&
          (!usesOwnApp || ownApps.length > 0)
            ? signInButton(submitCreate, usesOwnApp && !selectedOwnApp)
            : undefined
        }
        ownAppOption={
          sharedApp && !prefersOwnApp
            ? () => {
                setOwnAppError(undefined);
                setPrefersOwnApp(true);
                managed.setSelection({ mode: 'create' });
              }
            : undefined
        }
      />
      {createActions}
      <EmptyCredentialValuesConfirmDialog {...emptyValuesDialogProps} />
      {returnsToList && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          disabled={isSubmitting}
          onClick={() => {
            setOwnAppError(undefined);
            setPrefersOwnApp(false);
            managed.setSelection(null);
          }}
        >
          {t('credentials.connection.backToList')}
        </Button>
      )}
      {usesInlineCredentials && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          disabled={isSubmitting}
          onClick={() => {
            setOwnAppError(undefined);
            setPrefersOwnApp(false);
            managed.setSelection(null);
            onSelect(undefined);
            setIsChanging(false);
          }}
        >
          {t('credentials.connection.keepInline')}
        </Button>
      )}
    </Flex>
  );
}
